import React, { useEffect, useState, useRef } from 'react';
import { MessageCircleIcon, XIcon, SendIcon, AlertCircleIcon, CreditCardIcon, SmartphoneIcon, MonitorIcon, PlusCircleIcon, WrenchIcon, SearchIcon, CheckCircleIcon, UploadIcon, ArrowLeftIcon, CopyIcon, ExternalLinkIcon, BotIcon, Loader2Icon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { addTicket, getTicketById, getAIResponse, SupportTicket } from './SupportTicketStore';
interface ChatMessage {
  id: string;
  type: 'bot' | 'user' | 'system';
  content: string;
  timestamp: Date;
  actions?: QuickAction[];
  ticketInfo?: SupportTicket;
}
interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  action: string;
}
type FlowStep = 'welcome' | 'transaction-issue-1' | 'transaction-issue-2' | 'transaction-issue-3' | 'terminal-request-1' | 'terminal-request-2' | 'device-issue-1' | 'device-issue-2' | 'track-ticket' | 'ai-response' | 'ticket-created';
const MerchantSupportChatbot: React.FC = () => {
  const {
    user,
    isAuthenticated
  } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [currentFlow, setCurrentFlow] = useState<FlowStep>('welcome');
  const [flowData, setFlowData] = useState<Record<string, string>>({});
  const [isTyping, setIsTyping] = useState(false);
  const [copied, setCopied] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      initializeChat();
    }
  }, [isOpen]);
  const initializeChat = () => {
    const welcomeMessage: ChatMessage = {
      id: '1',
      type: 'bot',
      content: `Hello${user?.name ? `, ${user.name}` : ''}! I am your Merchant Support Assistant. How can I assist you today?`,
      timestamp: new Date(),
      actions: getQuickActions()
    };
    setMessages([welcomeMessage]);
    setCurrentFlow('welcome');
    setFlowData({});
  };
  const getQuickActions = (): QuickAction[] => [{
    id: 'transaction',
    label: 'Report Transaction Issue',
    icon: <CreditCardIcon className="h-4 w-4" data-id="element-770" />,
    action: 'transaction-issue'
  }, {
    id: 'portal',
    label: 'Merchant Portal Issue',
    icon: <MonitorIcon className="h-4 w-4" data-id="element-771" />,
    action: 'portal-issue'
  }, {
    id: 'mobile',
    label: 'Mobile App Issue',
    icon: <SmartphoneIcon className="h-4 w-4" data-id="element-772" />,
    action: 'mobile-issue'
  }, {
    id: 'terminal',
    label: 'Request New Terminal',
    icon: <PlusCircleIcon className="h-4 w-4" data-id="element-773" />,
    action: 'terminal-request'
  }, {
    id: 'device',
    label: 'Report Device Problem',
    icon: <WrenchIcon className="h-4 w-4" data-id="element-774" />,
    action: 'device-issue'
  }, {
    id: 'track',
    label: 'Track Existing Complaint',
    icon: <SearchIcon className="h-4 w-4" data-id="element-775" />,
    action: 'track-ticket'
  }];
  const addBotMessage = (content: string, actions?: QuickAction[], ticketInfo?: SupportTicket) => {
    setIsTyping(true);
    setTimeout(() => {
      const newMessage: ChatMessage = {
        id: Date.now().toString(),
        type: 'bot',
        content,
        timestamp: new Date(),
        actions,
        ticketInfo
      };
      setMessages(prev => [...prev, newMessage]);
      setIsTyping(false);
    }, 500);
  };
  const addUserMessage = (content: string) => {
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };
  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'transaction-issue':
      case 'portal-issue':
      case 'mobile-issue':
        addUserMessage('Report Transaction Issue');
        setCurrentFlow('transaction-issue-1');
        setFlowData({
          merchantId: user?.merchantId || '050102100023650'
        });
        addBotMessage(`I'll help you report a transaction issue.\n\n**Merchant ID:** ${user?.merchantId || '050102100023650'} (auto-filled)\n\nPlease provide the following details:\n\n**Transaction ID** (optional - enter or type "skip"):`);
        break;
      case 'terminal-request':
        addUserMessage('Request New Terminal');
        setCurrentFlow('terminal-request-1');
        setFlowData({
          merchantId: user?.merchantId || '050102100023650'
        });
        addBotMessage(`I'll help you request a new terminal.\n\n**Merchant ID:** ${user?.merchantId || '050102100023650'} (auto-filled)\n\nPlease select the **Terminal Type**:`, [{
          id: 'pos',
          label: 'POS Terminal',
          icon: <CreditCardIcon className="h-4 w-4" data-id="element-776" />,
          action: 'terminal-type-pos'
        }, {
          id: 'softpos',
          label: 'Soft POS',
          icon: <SmartphoneIcon className="h-4 w-4" data-id="element-777" />,
          action: 'terminal-type-softpos'
        }, {
          id: 'mobilepos',
          label: 'Mobile POS',
          icon: <SmartphoneIcon className="h-4 w-4" data-id="element-778" />,
          action: 'terminal-type-mobilepos'
        }, {
          id: 'qr',
          label: 'QR Code Terminal',
          icon: <MonitorIcon className="h-4 w-4" data-id="element-779" />,
          action: 'terminal-type-qr'
        }]);
        break;
      case 'device-issue':
        addUserMessage('Report Device Problem');
        setCurrentFlow('device-issue-1');
        setFlowData({
          merchantId: user?.merchantId || '050102100023650'
        });
        addBotMessage(`I'll help you report a device issue.\n\n**Merchant ID:** ${user?.merchantId || '050102100023650'} (auto-filled)\n\nPlease enter your **Terminal ID** (e.g., T-8472):`);
        break;
      case 'track-ticket':
        addUserMessage('Track Existing Complaint');
        setCurrentFlow('track-ticket');
        addBotMessage('Please enter your **Ticket/Complaint Number** (e.g., CMP-2026-000234):');
        break;
      // Terminal type selections
      case 'terminal-type-pos':
      case 'terminal-type-softpos':
      case 'terminal-type-mobilepos':
      case 'terminal-type-qr':
        const terminalType = action.replace('terminal-type-', '').toUpperCase();
        const terminalLabel = terminalType === 'POS' ? 'POS Terminal' : terminalType === 'SOFTPOS' ? 'Soft POS' : terminalType === 'MOBILEPOS' ? 'Mobile POS' : 'QR Code Terminal';
        addUserMessage(terminalLabel);
        setFlowData(prev => ({
          ...prev,
          terminalType: terminalLabel
        }));
        setCurrentFlow('terminal-request-2');
        addBotMessage(`Terminal Type: **${terminalLabel}**\n\nPlease select the **Reason for Request**:`, [{
          id: 'new-branch',
          label: 'New branch setup',
          icon: <PlusCircleIcon className="h-4 w-4" data-id="element-780" />,
          action: 'reason-new-branch'
        }, {
          id: 'expansion',
          label: 'Business expansion',
          icon: <PlusCircleIcon className="h-4 w-4" data-id="element-781" />,
          action: 'reason-expansion'
        }, {
          id: 'replacement',
          label: 'Replacement device',
          icon: <WrenchIcon className="h-4 w-4" data-id="element-782" />,
          action: 'reason-replacement'
        }]);
        break;
      // Reason selections
      case 'reason-new-branch':
      case 'reason-expansion':
      case 'reason-replacement':
        const reason = action === 'reason-new-branch' ? 'New branch setup' : action === 'reason-expansion' ? 'Business expansion' : 'Replacement device';
        addUserMessage(reason);
        handleTerminalRequestSubmit(reason);
        break;
      // Issue type selections
      case 'issue-failed':
      case 'issue-debited':
      case 'issue-duplicate':
      case 'issue-settlement':
      case 'issue-portal':
        const issueType = action === 'issue-failed' ? 'Transaction Failed' : action === 'issue-debited' ? 'Amount Debited but Not Received' : action === 'issue-duplicate' ? 'Duplicate Transaction' : action === 'issue-settlement' ? 'Settlement Issue' : 'Portal Error';
        addUserMessage(issueType);
        handleTransactionIssueSubmit(issueType);
        break;
      // Device issue category selections
      case 'device-power':
      case 'device-network':
      case 'device-timeout':
      case 'device-card':
      case 'device-printer':
        const deviceCategory = action === 'device-power' ? 'Device not powering on' : action === 'device-network' ? 'Network connectivity issue' : action === 'device-timeout' ? 'Transaction timeout' : action === 'device-card' ? 'Card reading issue' : 'Printer malfunction';
        addUserMessage(deviceCategory);
        handleDeviceIssueSubmit(deviceCategory);
        break;
      case 'new-request':
        initializeChat();
        break;
      case 'view-tickets':
        // This will be handled by the Link component
        break;
      default:
        break;
    }
  };
  const handleTransactionIssueSubmit = (issueType: string) => {
    const ticket = addTicket({
      type: 'complaint',
      category: issueType,
      description: `${issueType} reported via chatbot`,
      merchantId: flowData.merchantId || user?.merchantId || '050102100023650',
      priority: issueType.includes('Debited') || issueType.includes('Settlement') ? 'High' : 'Medium',
      details: {
        transactionId: flowData.transactionId || 'Not provided',
        transactionDate: flowData.transactionDate || new Date().toISOString().slice(0, 10),
        amount: flowData.amount || 'Not specified',
        channel: flowData.channel || 'Not specified',
        issueType
      }
    });
    setCurrentFlow('ticket-created');
    addBotMessage(`✅ **Your complaint has been successfully registered.**\n\n**Complaint Number:** \`${ticket.id}\`\n\nOur support team will review the request and contact you if required.\n\n**Expected Resolution:** ${ticket.expectedResolution}`, [{
      id: 'new',
      label: 'Create New Request',
      icon: <PlusCircleIcon className="h-4 w-4" data-id="element-783" />,
      action: 'new-request'
    }, {
      id: 'view',
      label: 'View All Tickets',
      icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-784" />,
      action: 'view-tickets'
    }], ticket);
  };
  const handleTerminalRequestSubmit = (reason: string) => {
    const ticket = addTicket({
      type: 'terminal-request',
      category: 'New Terminal',
      description: `Terminal request: ${flowData.terminalType} - ${reason}`,
      merchantId: flowData.merchantId || user?.merchantId || '050102100023650',
      priority: 'Medium',
      details: {
        terminalType: flowData.terminalType || 'POS Terminal',
        quantity: '1',
        branch: flowData.branch || 'Main Branch',
        reason
      }
    });
    setCurrentFlow('ticket-created');
    addBotMessage(`✅ **Your terminal request has been submitted successfully.**\n\n**Request ID:** \`${ticket.id}\`\n\nOur onboarding team will process your request shortly.\n\n**Expected Processing:** ${ticket.expectedResolution}`, [{
      id: 'new',
      label: 'Create New Request',
      icon: <PlusCircleIcon className="h-4 w-4" data-id="element-785" />,
      action: 'new-request'
    }, {
      id: 'view',
      label: 'View All Tickets',
      icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-786" />,
      action: 'view-tickets'
    }], ticket);
  };
  const handleDeviceIssueSubmit = (category: string) => {
    const isCritical = category.includes('power') || category.includes('network');
    const ticket = addTicket({
      type: 'device-issue',
      category,
      description: `Device issue: ${category} on terminal ${flowData.terminalId}`,
      merchantId: flowData.merchantId || user?.merchantId || '050102100023650',
      priority: isCritical ? 'High' : 'Medium',
      details: {
        terminalId: flowData.terminalId || 'Not specified',
        terminalType: flowData.terminalType || 'POS',
        issueCategory: category,
        errorMessage: flowData.errorMessage || 'Not provided'
      }
    });
    setCurrentFlow('ticket-created');
    addBotMessage(`✅ **Your device issue has been successfully logged.**\n\n**Ticket ID:** \`${ticket.id}\`\n\nOur technical team will review your request.\n\n**Expected Resolution:** ${ticket.expectedResolution}`, [{
      id: 'new',
      label: 'Create New Request',
      icon: <PlusCircleIcon className="h-4 w-4" data-id="element-787" />,
      action: 'new-request'
    }, {
      id: 'view',
      label: 'View All Tickets',
      icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-788" />,
      action: 'view-tickets'
    }], ticket);
  };
  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    const message = inputValue.trim();
    addUserMessage(message);
    setInputValue('');
    // Handle based on current flow
    switch (currentFlow) {
      case 'welcome':
        // Try AI knowledge base first
        const aiResponse = getAIResponse(message);
        if (aiResponse) {
          setCurrentFlow('ai-response');
          addBotMessage(`${aiResponse}\n\n---\n\nWas this helpful? If you need further assistance, please select an option below:`, getQuickActions());
        } else {
          addBotMessage("I couldn't find a specific answer to your question. Let me connect you with the right support option:", getQuickActions());
        }
        break;
      case 'transaction-issue-1':
        setFlowData(prev => ({
          ...prev,
          transactionId: message === 'skip' ? '' : message
        }));
        setCurrentFlow('transaction-issue-2');
        addBotMessage('Please enter the **Transaction Amount** (in PKR):');
        break;
      case 'transaction-issue-2':
        setFlowData(prev => ({
          ...prev,
          amount: message
        }));
        setCurrentFlow('transaction-issue-3');
        addBotMessage('Please select the **Issue Type**:', [{
          id: 'failed',
          label: 'Transaction Failed',
          icon: <AlertCircleIcon className="h-4 w-4" data-id="element-789" />,
          action: 'issue-failed'
        }, {
          id: 'debited',
          label: 'Amount Debited but Not Received',
          icon: <AlertCircleIcon className="h-4 w-4" data-id="element-790" />,
          action: 'issue-debited'
        }, {
          id: 'duplicate',
          label: 'Duplicate Transaction',
          icon: <AlertCircleIcon className="h-4 w-4" data-id="element-791" />,
          action: 'issue-duplicate'
        }, {
          id: 'settlement',
          label: 'Settlement Issue',
          icon: <AlertCircleIcon className="h-4 w-4" data-id="element-792" />,
          action: 'issue-settlement'
        }, {
          id: 'portal',
          label: 'Portal Error',
          icon: <AlertCircleIcon className="h-4 w-4" data-id="element-793" />,
          action: 'issue-portal'
        }]);
        break;
      case 'device-issue-1':
        setFlowData(prev => ({
          ...prev,
          terminalId: message
        }));
        setCurrentFlow('device-issue-2');
        addBotMessage(`Terminal ID: **${message}**\n\nPlease select the **Issue Category**:`, [{
          id: 'power',
          label: 'Device not powering on',
          icon: <AlertCircleIcon className="h-4 w-4" data-id="element-794" />,
          action: 'device-power'
        }, {
          id: 'network',
          label: 'Network connectivity issue',
          icon: <AlertCircleIcon className="h-4 w-4" data-id="element-795" />,
          action: 'device-network'
        }, {
          id: 'timeout',
          label: 'Transaction timeout',
          icon: <AlertCircleIcon className="h-4 w-4" data-id="element-796" />,
          action: 'device-timeout'
        }, {
          id: 'card',
          label: 'Card reading issue',
          icon: <AlertCircleIcon className="h-4 w-4" data-id="element-797" />,
          action: 'device-card'
        }, {
          id: 'printer',
          label: 'Printer malfunction',
          icon: <AlertCircleIcon className="h-4 w-4" data-id="element-798" />,
          action: 'device-printer'
        }]);
        break;
      case 'track-ticket':
        const ticket = getTicketById(message.toUpperCase());
        if (ticket) {
          const statusColor = ticket.status === 'Resolved' ? '🟢' : ticket.status === 'Under Review' ? '🟡' : ticket.status === 'Open' ? '🔵' : '⚪';
          addBotMessage(`**Ticket Found!**\n\n**Ticket ID:** ${ticket.id}\n**Status:** ${statusColor} ${ticket.status}\n**Category:** ${ticket.category}\n**Priority:** ${ticket.priority}\n**Created:** ${ticket.createdDate}\n**Last Updated:** ${ticket.lastUpdated}\n**Support Team:** ${ticket.supportTeam}\n**Expected Resolution:** ${ticket.expectedResolution || 'TBD'}\n\n---\n\nNeed more help?`, [{
            id: 'new',
            label: 'Create New Request',
            icon: <PlusCircleIcon className="h-4 w-4" data-id="element-799" />,
            action: 'new-request'
          }, {
            id: 'view',
            label: 'View All Tickets',
            icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-800" />,
            action: 'view-tickets'
          }]);
        } else {
          addBotMessage(`❌ No ticket found with ID: **${message}**\n\nPlease check the ticket number and try again, or create a new request.`, [{
            id: 'track',
            label: 'Try Again',
            icon: <SearchIcon className="h-4 w-4" data-id="element-801" />,
            action: 'track-ticket'
          }, {
            id: 'new',
            label: 'Create New Request',
            icon: <PlusCircleIcon className="h-4 w-4" data-id="element-802" />,
            action: 'new-request'
          }]);
        }
        setCurrentFlow('welcome');
        break;
      case 'ai-response':
      case 'ticket-created':
        // Try AI knowledge base
        const response = getAIResponse(message);
        if (response) {
          addBotMessage(`${response}\n\n---\n\nAnything else I can help with?`, getQuickActions());
        } else {
          addBotMessage('How else can I assist you?', getQuickActions());
        }
        setCurrentFlow('welcome');
        break;
      default:
        addBotMessage("I'm here to help! Please select an option:", getQuickActions());
        setCurrentFlow('welcome');
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  const copyTicketId = (ticketId: string) => {
    navigator.clipboard.writeText(ticketId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  if (!isAuthenticated) return null;
  return <>
      {/* Floating Chat Button */}
      <button onClick={() => setIsOpen(true)} className={`fixed bottom-6 right-6 z-50 w-14 h-14 bg-gradient-to-br from-indigo-600 to-violet-600 text-white rounded-full shadow-lg shadow-indigo-500/30 flex items-center justify-center hover:scale-110 transition-all duration-300 ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`} aria-label="Open Support Chat" data-id="element-803">
        <MessageCircleIcon className="h-6 w-6" data-id="element-804" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" data-id="element-805"></span>
      </button>

      {/* Chat Panel */}
      <div className={`fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-48px)] h-[600px] max-h-[calc(100vh-100px)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden transition-all duration-300 ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'}`} data-id="element-806">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white p-4 flex items-center justify-between flex-shrink-0" data-id="element-807">
          <div className="flex items-center space-x-3" data-id="element-808">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center" data-id="element-809">
              <BotIcon className="h-5 w-5" data-id="element-810" />
            </div>
            <div data-id="element-811">
              <h3 className="font-semibold text-sm" data-id="element-812">
                Merchant Support Assistant
              </h3>
              <div className="flex items-center space-x-1.5 text-xs text-indigo-100" data-id="element-813">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" data-id="element-814"></span>
                <span data-id="element-815">AI Support Online</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2" data-id="element-816">
            <Link to="/support-tickets" onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" title="View All Tickets" data-id="element-817">
              <ExternalLinkIcon className="h-4 w-4" data-id="element-818" />
            </Link>
            <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-id="element-819">
              <XIcon className="h-5 w-5" data-id="element-820" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50" data-id="element-821">
          {messages.map(msg => <div key={msg.id} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-822">
              <div className={`max-w-[85%] ${msg.type === 'user' ? 'bg-indigo-600 text-white rounded-2xl rounded-br-md' : 'bg-white border border-slate-200 text-slate-700 rounded-2xl rounded-bl-md shadow-sm'} px-4 py-3`} data-id="element-823">
                <div className="text-sm whitespace-pre-wrap leading-relaxed" data-id="element-824">
                  {msg.content.split('**').map((part, i) => i % 2 === 1 ? <strong key={i} className={msg.type === 'user' ? 'text-white' : 'text-slate-900'} data-id="element-825">
                        {part}
                      </strong> : <span key={i} data-id="element-826">{part}</span>)}
                </div>

                {/* Ticket Info with Copy Button */}
                {msg.ticketInfo && <div className="mt-3 pt-3 border-t border-slate-200/50" data-id="element-827">
                    <button onClick={() => copyTicketId(msg.ticketInfo!.id)} className="flex items-center space-x-2 text-xs bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors" data-id="element-828">
                      {copied ? <>
                          <CheckCircleIcon className="h-3.5 w-3.5 text-green-600" data-id="element-829" />
                          <span className="text-green-600 font-medium" data-id="element-830">
                            Copied!
                          </span>
                        </> : <>
                          <CopyIcon className="h-3.5 w-3.5 text-slate-500" data-id="element-831" />
                          <span className="text-slate-600 font-medium" data-id="element-832">
                            Copy Ticket ID
                          </span>
                        </>}
                    </button>
                  </div>}

                {/* Quick Actions */}
                {msg.actions && msg.actions.length > 0 && <div className="mt-3 pt-3 border-t border-slate-100 space-y-2" data-id="element-833">
                    {msg.actions.map(action => action.action === 'view-tickets' ? <Link key={action.id} to="/support-tickets" onClick={() => setIsOpen(false)} className="flex items-center space-x-2 w-full px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-sm font-medium text-slate-700 transition-colors" data-id="element-834">
                          {action.icon}
                          <span data-id="element-835">{action.label}</span>
                        </Link> : <button key={action.id} onClick={() => handleQuickAction(action.action)} className="flex items-center space-x-2 w-full px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-lg text-sm font-medium text-slate-700 transition-colors" data-id="element-836">
                          {action.icon}
                          <span data-id="element-837">{action.label}</span>
                        </button>)}
                  </div>}

                <div className={`text-xs mt-2 ${msg.type === 'user' ? 'text-indigo-200' : 'text-slate-400'}`} data-id="element-838">
                  {msg.timestamp.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
                </div>
              </div>
            </div>)}

          {isTyping && <div className="flex justify-start" data-id="element-839">
              <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm" data-id="element-840">
                <div className="flex space-x-1" data-id="element-841">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-842"></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-843"></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-844"></div>
                </div>
              </div>
            </div>}

          <div ref={messagesEndRef} data-id="element-845" />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-200 bg-white flex-shrink-0" data-id="element-846">
          <div className="flex items-center space-x-2" data-id="element-847">
            <div className="flex-1 relative" data-id="element-848">
              <input ref={inputRef} type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder="Type your message..." className="w-full px-4 py-2.5 bg-slate-100 border border-transparent rounded-xl text-sm focus:bg-white focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all outline-none" data-id="element-849" />
            </div>
            <button onClick={handleSendMessage} disabled={!inputValue.trim()} className="p-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-850">
              <SendIcon className="h-5 w-5" data-id="element-851" />
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-2 text-center" data-id="element-852">
            Powered by AI • Your data is secure and encrypted
          </p>
        </div>
      </div>
    </>;
};
export default MerchantSupportChatbot;